import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { supabase } from '@/shared/lib/auth'

type RegisterFlowState = {
  step: 1 | 2 | 3
  email: string
  isLoading: boolean
  error: string | null
}

const initialState: RegisterFlowState = {
  step: 1,
  email: '',
  isLoading: false,
  error: null,
}

export function useRegisterFlow(redirectDest: string, initialError?: string) {
  const navigate = useNavigate()
  const [state, setState] = useState<RegisterFlowState>(() => ({
    ...initialState,
    error: initialError ?? null,
  }))

  function setLoading() {
    setState(prev => ({ ...prev, isLoading: true, error: null }))
  }

  function setError(error: string) {
    setState(prev => ({ ...prev, isLoading: false, error }))
  }

  async function sendOtp(email: string) {
    setLoading()
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: true },
    })
    if (error) {
      setError('Não foi possível enviar o código. Verifique o e-mail e tente novamente.')
      return
    }
    setState(prev => ({ ...prev, email, step: 2, isLoading: false, error: null }))
  }

  async function verifyOtp(otp: string) {
    setLoading()
    const { data, error } = await supabase.auth.verifyOtp({
      email: state.email,
      token: otp,
      type: 'email',
    })
    if (error || !data.session) {
      const message = error?.message?.toLowerCase().includes('expired')
        ? 'Código expirado. Solicite um novo clicando em Reenviar.'
        : 'Código incorreto. Tente novamente.'
      setError(message)
      return
    }
    const isNewUser = !data.user?.user_metadata?.['display_name']
    if (isNewUser) {
      setState(prev => ({ ...prev, step: 3, isLoading: false, error: null }))
    } else {
      window.location.replace(redirectDest)
    }
  }

  async function resendOtp() {
    if (!state.email) return
    setLoading()
    const { error } = await supabase.auth.signInWithOtp({
      email: state.email,
      options: { shouldCreateUser: true },
    })
    if (error) {
      setError('Não foi possível reenviar o código. Tente novamente.')
      return
    }
    setState(prev => ({ ...prev, isLoading: false, error: null }))
  }

  async function saveProfile(nickname: string) {
    setLoading()
    const { error } = await supabase.auth.updateUser({
      data: { display_name: nickname },
    })
    if (error) {
      setError('Não foi possível salvar o perfil. Tente novamente.')
      return
    }
    window.location.replace(redirectDest)
  }

  function goBack() {
    setState(prev => ({
      ...prev,
      step: Math.max(1, prev.step - 1) as 1 | 2 | 3,
      error: null,
    }))
  }

  // Expose navigate for any component that needs typed router navigation
  function navigateTo(to: '/home') {
    void navigate({ to })
  }

  return {
    step: state.step,
    email: state.email,
    isLoading: state.isLoading,
    error: state.error,
    sendOtp,
    verifyOtp,
    resendOtp,
    saveProfile,
    goBack,
    navigateTo,
  }
}

export type RegisterFlow = ReturnType<typeof useRegisterFlow>
